import * as requestFromServer from './lawCrud';
import {lawAgentsSlice, callTypes} from './lawAgentsSlice';
import {lawTransactionsSlice} from './lawTransactionsSlice';
import {RequestCancellationManager} from '../../_common/utils/RequestCancellationManager';
import {v4 as guid} from 'uuid';
import {LawAgentsResponse, LawTransactionsResponse} from "../types";
import {logUserAction} from "../../../../utils";
import {pages as P} from "../../_common/constants/pages";
import {subjects as S} from "../../_common/constants/subjects";
import {actionTypes as T} from "../../_common/constants/actionTypes";
import {logMessages as L} from "../../_common/constants/logMessages";

export const geLawAgentsListIDPrefix = 'geLawAgentsListIDPrefix';
export const geLawTransactionsListIDPrefix = 'geLawTransactionsListIDPrefix';
const {actions: lawAgentsActions} = lawAgentsSlice;
const {actions: lawTransactionsActions} = lawTransactionsSlice;

interface QueryParams {
  pageNumber?: number;
  pageSize?: number;
}
type Dispatch = (action: any) => void;

const getLawAgentsList =
  (storeMethod: any) =>
    (queryParams: QueryParams) =>
      (dispatch: Dispatch) => {
        dispatch(lawAgentsActions.startCall({callType: callTypes.list}));

        const requestID = `${geLawAgentsListIDPrefix}-${guid()}`;
        const cancellationHandlers = RequestCancellationManager.createCancellationHandlers(requestID);

        return requestFromServer
          .getLawAgents(queryParams, cancellationHandlers)
          .then((response: { data: LawAgentsResponse }) => {
            const {errorCode, data: {totalCount, list, pageCount, totalPaid}} = response.data;

            if (errorCode !== 0) throw new Error();

            dispatch(
              storeMethod({
                totalCount: totalCount || 0,
                pageCount: pageCount || 0,
                entities: list || [],
                totalPaid
              })
            );
          })
          .catch((error: any) => {
            error.clientMessage = "Can't find law agents data";
            dispatch(lawAgentsActions.catchError({error, callType: callTypes.list}));
          })
          .finally(() => {
            RequestCancellationManager.forgetRequest(requestID);
          });
      };

export const getLawAgents = getLawAgentsList(lawAgentsActions.lawAgentsFetched);
export const getNextLawAgents = getLawAgentsList(lawAgentsActions.lawAgentsAppended);

const getLawTransactionsList =
  (storeMethod: any) =>
    (queryParams: QueryParams) =>
      (dispatch: Dispatch) => {
        dispatch(lawAgentsActions.startCall({callType: callTypes.list}));

        const requestID = `${geLawTransactionsListIDPrefix}-${guid()}`;
        const cancellationHandlers = RequestCancellationManager.createCancellationHandlers(requestID);

        return requestFromServer
          .getLawTransactions(queryParams, cancellationHandlers)
          .then((response: { data: LawTransactionsResponse }) => {
            const {errorCode, data: {totalCount, list, pageCount, totalPaid}} = response.data;

            if (errorCode !== 0) throw new Error();

            dispatch(
              storeMethod({
                totalCount: totalCount || 0,
                pageCount: pageCount || 0,
                entities: list || [],
                totalPaid
              })
            );
          })
          .catch((error: any) => {
            error.clientMessage = "Can't find law transactions data";
            dispatch(lawTransactionsActions.catchError({error, callType: callTypes.list}));
          })
          .finally(() => {
            RequestCancellationManager.forgetRequest(requestID);
          });
      };

export const getLawTransactions = getLawTransactionsList(lawTransactionsActions.lawTransactionsFetched);
export const getNextLawTransactions = getLawTransactionsList(lawTransactionsActions.lawTransactionsAppended);

export const getLawAgentsForExcelFile = (queryParams: QueryParams) => (dispatch: Dispatch) => {
  dispatch(lawAgentsActions.startCall({callType: callTypes.action}));

  return requestFromServer
    .getLawAgentsForExcelFile(queryParams)
    .then((response: any) => {
      const {errorCode, data} = response.data;

      if (errorCode !== 0) throw new Error();

      return data?.list ?? [];
    })
    .catch((error: any) => {
      console.error({error});
      error.clientMessage = "Can't get data!";
      dispatch(lawAgentsActions.catchError({error, callType: callTypes.action}));
    });
};

export const getLawTransactionsForExcelFile = (queryParams: QueryParams) => (dispatch: Dispatch) => {
  dispatch(lawAgentsActions.startCall({callType: callTypes.action}));

  return requestFromServer
    .getLawTransactionsForExcelFile(queryParams)
    .then((response: any) => {
      const {errorCode, data} = response.data;

      if (errorCode !== 0) throw new Error();

      return data?.list ?? [];
    })
    .catch((error: any) => {
      console.error({error});
      error.clientMessage = "Can't get data!";
      dispatch(lawTransactionsActions.catchError({error, callType: callTypes.action}));
    });
};

export const uploadLawTransactions = async (file: File) => {
  const formData = new FormData();
  formData.append('file', file);

  return requestFromServer.uploadLawTransactions(formData)
    .then((response) => {
      const {errorCode, data} = response.data;
      logUserAction(
        P.LAW,
        S.LAW_TRANSACTION,
        T.EDIT,
        L.LAW_TRANSACTIONS_UPLOAD(file?.name),
        errorCode
      );

      if (errorCode === 0 || errorCode === '0') {
        return data;
      } else {
        return [];
        throw new Error();
      };
    })
    .catch((error: any) => {
      error.clientMessage = "Can't upload transactions data";
    })
};